/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { DefaultButton, IButtonProps, IButtonStyles, PrimaryButton } from '@fluentui/react/lib/Button';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { TrainingStore } from 'src/luis/core/stores/TrainingStore';
import { useCurrentVersionedAppId } from 'src/luis/modules/apps/hooks/useCurrentVersionedAppId';
import { useCompositeEntityMigration } from 'src/luis/modules/entities/hooks/useCompositeEntityMigration/useCompositeEntityMigration';
import { CompositeEntitiesMigrationTrackerStore } from 'src/luis/types/stores/entities/CompositeEntitiesMigrationTrackerStore';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';
import { CompositeEntitiesScreen } from 'src/luis/verticals/entityMigrationModal/CompositeEntitiesScreen';
import { EntityMigrationDoneScreen } from 'src/luis/verticals/entityMigrationModal/EntityMigrationDoneScreen';
import { EntityMigrationLoadingScreen } from 'src/luis/verticals/entityMigrationModal/EntityMigrationLoadingScreen';
import { entityMigrationModalMessages, getScreenText } from 'src/luis/verticals/entityMigrationModal/entityMigrationModalMessages';
import { IntroScreen } from 'src/luis/verticals/entityMigrationModal/IntroScreen';
import { TrainingScreen } from 'src/luis/verticals/entityMigrationModal/TrainingScreen';

export type EntityMigrationModalProps = BasicModalProps<{ upgradedVersionId: string }> & {
    /**
     * The list of composite entities to upgrade.
     */
    compositeEntities: CompositeEntityStore[];

    /**
     * A callback fires when cancel button in the done screen
     * clicked.
     */
    onSuppressMigration: () => void;
};

const upgradeButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { marginRight: '10px' } });

/**
 * Generates the upgraded version name to be unique.
 *
 * @param currentName the current version name.
 * @param suffix the suffix of the upgraded version name.
 * @param versionNames Array of the other versions names.
 */
const getUpgradedVersionName = (currentName: string, suffix: string, versionNames: string[]): string => {
    let newVersionId = `${currentName}_${suffix}`;
    let versionDuplicateCount = 0;
    while (versionNames.includes(newVersionId)) {
        newVersionId = `${currentName}_${suffix} [${++versionDuplicateCount}]`;
    }

    return newVersionId;
};

/**
 * Displays the entity migration modal.
 */
export const EntityMigrationModal = observer((props: EntityMigrationModalProps) => {
    const { compositeEntities, onSuppressMigration, onDismiss, ...rest } = props;
    const { current: migrationTracker } = React.useRef(new CompositeEntitiesMigrationTrackerStore());
    const upgradedVersionTrainingStore = React.useRef<TrainingStore>();

    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const { appId, versionId } = useCurrentVersionedAppId();
    const { addMigrationFailureToast, addTrainingFailureToast } = useCompositeEntityMigration();

    const upgradedVersionId = React.useMemo(
        () =>
            getUpgradedVersionName(
                versionId,
                localize(entityMigrationModalMessages.upgraded),
                rootStore.currentLuisAppStore.appVersionStoreHouse.items.map(item => item.id)
            ),
        []
    );

    const [currentScreenId, setCurrentScreenId] = React.useState(0);
    const [isActiveVersionNeedsTraining, setIsActiveVersionNeedsTraining] = React.useState(true);
    const [compositeEntitiesIdsToMigrate, setCompositeEntitiesIdsToMigrate] = React.useState<string[]>([]);

    const migrationProgressTracker = useProgressTracker();
    const isMigrationFailed = migrationTracker.state === 'migrationFailed' || migrationProgressTracker.isFailed;
    const inDoneScreenWithMigrationFailed = currentScreenId === 4 && isMigrationFailed;

    const trainingTracker = useProgressTracker({
        onComplete: () =>
            dispatcher.dispatch('migrateCompositeEntities', {
                migrationTracker,
                upgradedVersionId,
                tracker: migrationProgressTracker,
                compositesNames: compositeEntities.filter(c => compositeEntitiesIdsToMigrate.includes(c.id as string)).map(c => c.name)
            })
    });

    const isUpgradedVersionTrainingFailed = upgradedVersionTrainingStore.current?.currentTrainingStatus.overallStatus === 'fail';
    const modalTitle = getScreenText(currentScreenId, trainingTracker.isFailed || isUpgradedVersionTrainingFailed, isMigrationFailed).title;

    React.useEffect(() => setCompositeEntitiesIdsToMigrate(compositeEntities.map<string>(e => e.id as string)), [compositeEntities]);

    React.useEffect(() => {
        if (migrationTracker.state === 'trainingNewVersion') {
            upgradedVersionTrainingStore.current = rootStore.luisAppStoreHouse
                .findById(appId)
                ?.appVersionStoreHouse.findById(upgradedVersionId)?.trainingStore;
        }

        if (isMigrationFailed) {
            addMigrationFailureToast();
        }
    }, [migrationTracker.state, migrationProgressTracker.isFailed]);

    React.useEffect(() => {
        if (upgradedVersionTrainingStore.current?.currentTrainingStatus.overallStatus === 'fail') {
            addTrainingFailureToast(upgradedVersionId);
        }
    }, [upgradedVersionTrainingStore.current?.currentTrainingStatus.overallStatus]);

    const handleSubmissions = () => {
        let nextScreen = currentScreenId + 1;

        switch (currentScreenId) {
            case 1:
                if (!rootStore.currentLuisAppStore.currentAppVersionStore.isDirty) {
                    nextScreen += 1;
                    dispatcher.dispatch('migrateCompositeEntities', {
                        migrationTracker,
                        upgradedVersionId,
                        tracker: migrationProgressTracker,
                        compositesNames: compositeEntities
                            .filter(c => compositeEntitiesIdsToMigrate.includes(c.id as string))
                            .map(c => c.name)
                    });
                }
                break;
            case 2:
                if (isActiveVersionNeedsTraining) {
                    dispatcher.dispatch('train', { tracker: trainingTracker });
                } else {
                    dispatcher.dispatch('migrateCompositeEntities', {
                        migrationTracker,
                        upgradedVersionId,
                        tracker: migrationProgressTracker,
                        compositesNames: compositeEntities
                            .filter(c => compositeEntitiesIdsToMigrate.includes(c.id as string))
                            .map(c => c.name)
                    });
                }
                break;
            case 4:
                getStorage('sessionStorage').setData(LuisConstants.StorageKeys.ConfirmSuccessfulMigrationStorageKey, 'true');
                dispatcher.dispatch('activateAppVersion', {
                    redirectUrl: `${routeTable.luis.entitiesPage(appId, upgradedVersionId)}`,
                    context: { versionId: upgradedVersionId }
                });
                break;
            default:
        }
        setCurrentScreenId(nextScreen);
    };

    /**
     * Displays the screen content depending on the current active screen.
     */
    const getScreenContent = () => {
        switch (currentScreenId) {
            case 0:
                return <IntroScreen />;
            case 1:
                return (
                    <CompositeEntitiesScreen
                        compositeEntities={compositeEntities}
                        selectedIds={compositeEntitiesIdsToMigrate}
                        onSelectionChange={(id, checked) =>
                            setCompositeEntitiesIdsToMigrate(
                                checked
                                    ? [...compositeEntitiesIdsToMigrate, id]
                                    : compositeEntitiesIdsToMigrate.filter(entityId => entityId !== id)
                            )
                        }
                    />
                );
            case 2:
                return (
                    <TrainingScreen
                        versionName={versionId}
                        onChange={setIsActiveVersionNeedsTraining}
                        needsTraining={isActiveVersionNeedsTraining}
                    />
                );
            case 3:
                return (
                    <EntityMigrationLoadingScreen
                        upgradedVersionName={upgradedVersionId}
                        migrationState={migrationTracker.state}
                        migrationStateProgress={migrationTracker.stateProgressPercentage}
                        isLoadingFinished={!trainingTracker.isRunning && !migrationProgressTracker.isRunning}
                    />
                );
            case 4:
            default:
                return (
                    <EntityMigrationDoneScreen
                        oldVersionName={versionId}
                        isMigrationFailed={isMigrationFailed}
                        isOldVersionTrainingFailed={trainingTracker.isFailed}
                        migrationFailureReason={migrationTracker.failureReason}
                        isNewVersionTrainingFailed={isUpgradedVersionTrainingFailed}
                    />
                );
        }
    };

    /**
     * Displays the modal footer with suitable text related to active screen.
     */
    const renderFooter = () => (
        <>
            {!inDoneScreenWithMigrationFailed && (
                <PrimaryButton
                    onClick={handleSubmissions}
                    styles={upgradeButtonStyles}
                    text={localize(getScreenText(currentScreenId, trainingTracker.isFailed, isMigrationFailed).submitButtonText)}
                    disabled={trainingTracker.isRunning || migrationProgressTracker.isRunning}
                />
            )}
            <DefaultButton onClick={currentScreenId === 4 ? onDismiss : onSuppressMigration}>
                {localize(getScreenText(currentScreenId, trainingTracker.isFailed, isMigrationFailed).cancelButtonText)}
            </DefaultButton>
        </>
    );

    return (
        <BasicModal
            {...rest}
            onRenderFooter={renderFooter}
            disabled={currentScreenId > 4}
            dialogContentProps={{ title: (modalTitle && localize(modalTitle)) || '' }}
            onDismiss={currentScreenId === 4 ? onDismiss : onSuppressMigration}>
            <Announced aria-live="assertive" message={modalTitle && localize(modalTitle)} />
            {getScreenContent()}
        </BasicModal>
    );
});
